import { createSelector, createSlice } from '@reduxjs/toolkit';
import { sortItems } from '@utils/functions.js';

/** FormBlock.Data arrives as text (or already parsed, depending on which payload built it). */
const parseBlockData = (data) => {
  if (!data) return {};
  if (typeof data === 'object') return data;

  try {
    return JSON.parse(data) ?? {};
  } catch {
    return {};
  }
};

const initialState = {
  isLoading: false,
  isActive: false,
  id: 0,
  uid: 0,
  sid: 0,

  blockContent: {
    isFieldName: false,
    isFieldNameRequired: false,
    fieldName: 'Name',

    isFieldEmail: false,
    isFieldEmailRequired: false,
    fieldEmail: 'Email address',

    isFieldPhone: false,
    isFieldPhoneRequired: false,
    fieldPhone: 'Phone',

    isFieldAddress: false,
    isFieldAddressRequired: false,
    fieldAddress: 'Address',

    isFieldComment: false,
    isFieldCommentRequired: false,
    fieldComment: 'Comment',

    items: [],
    buttonText: '',

    isTerms: false,
    termsTitle: '',
    termsUrl: '',

    resultActionType: 'none',
    resultAttachments: [],
    resultData: '',
    resultTitle: '',
    successMessage: '',

    // Template data — one JSON column (FormBlock.Data). Holds the closed state.
    data: {},

    heading: {
      title: '',
      description: '',
      textAlign: 'left',
      image: '',
      divider: '',
      position: '',
    },

    // Order Form (BlockType.OrderForm). formType === 'order' активує UI замовлення.
    formType: 'default',
    paymentMode: 'invoice',
    currency: 'USD',
    taxRate: null,
    shippingFee: null,
    depositPercent: null,
    lineItems: [], // [{ id, name, description, price, isFixedQuantity, minQuantity, maxQuantity, defaultQuantity, n, isActive }]
  },
  blockDesign: {
    view: 'card',
  },
  blockPreview: {
    isActive: false,
    view: 'compact',
    thumbnail: null,
    title: '',
    description: '',
    button: '',
    label: '',
    timeStart: null,
  },
  fullBlockDesign: {},
  itemDesign: {},
  buttonDesign: {},
  containerDesign: {},
  previewDesign: {},
};

export const formSlice = createSlice({
  name: 'form',
  initialState,
  reducers: {
    formStore: (state, action) => {
      const { id, isActive, sid, uid, data, preview } = action.payload;

      const block = data ?? {};
      const { blockContent, blockDesign } = block;
      const { itemDesign, buttonDesign, containerDesign, previewDesign } = blockDesign;

      state.isLoading = false;
      state.id = id ?? 0;
      state.isActive = isActive;
      state.sid = sid;
      state.uid = uid;

      state.itemDesign = itemDesign ?? {};
      state.buttonDesign = buttonDesign ?? {};
      state.previewDesign = previewDesign ?? {};
      state.containerDesign = containerDesign ?? {};
      state.blockPreview = {
        isActive: preview?.isActive || false,
        thumbnail: preview?.thumbnail ?? null,
        view: preview?.view ?? 'compact',
        title: preview?.title ?? '',
        description: preview?.description ?? '',
        button: preview?.button ?? '',
        label: preview?.label ?? '',
        timeStart: preview?.timeStart ?? null,
      };

      state.blockContent = {
        isFieldName: blockContent.isFieldName ?? false,
        isFieldNameRequired: blockContent.isFieldNameRequired ?? false,
        fieldName: blockContent.fieldName ?? 'Name',

        isFieldEmail: blockContent.isFieldEmail ?? false,
        isFieldEmailRequired: blockContent.isFieldEmailRequired ?? false,
        fieldEmail: blockContent.fieldEmail ?? 'Email address',

        isFieldPhone: blockContent.isFieldPhone ?? false,
        isFieldPhoneRequired: blockContent.isFieldPhoneRequired ?? false,
        fieldPhone: blockContent.fieldPhone ?? 'Phone',

        isFieldAddress: blockContent.isFieldAddress ?? false,
        isFieldAddressRequired: blockContent.isFieldAddressRequired ?? false,
        fieldAddress: blockContent.fieldAddress ?? 'Address',

        isFieldComment: blockContent.isFieldComment ?? false,
        isFieldCommentRequired: blockContent.isFieldCommentRequired ?? false,
        fieldComment: blockContent.fieldComment ?? 'Comment',

        items: blockContent.items ?? [],
        buttonText: blockContent.buttonText ?? '',

        isTerms: blockContent.isTerms ?? false,
        termsTitle: blockContent.termsTitle ?? '',
        termsUrl: blockContent.termsUrl ?? '',

        resultActionType: blockContent.resultActionType ?? 'none',
        resultAttachments: blockContent.resultAttachments ?? [],
        resultData: blockContent.resultData ?? '',
        resultTitle: blockContent.resultTitle ?? '',
        successMessage: blockContent.successMessage ?? '',

        // Core stores this as text; parse once here so the panel edits an object.
        data: parseBlockData(blockContent.data),

        heading: {
          ...blockContent.heading,
          title: blockContent.heading.title ?? '',
          description: blockContent.heading.description ?? '',
          textAlign: blockContent.heading.textAlign ?? 'left',
          image: blockContent.heading.image ?? '',
          divider: blockContent.heading.divider ?? '',
          position: blockContent.heading.position ?? '',
          label: blockContent.heading.label ?? '',
          cta: blockContent.heading.cta ?? [],
        },

        formType: blockContent.formType ?? 'default',
        paymentMode: blockContent.paymentMode ?? 'invoice',
        currency: blockContent.currency ?? 'USD',
        taxRate: blockContent.taxRate ?? null,
        shippingFee: blockContent.shippingFee ?? null,
        depositPercent: blockContent.depositPercent ?? null,
        lineItems: blockContent.lineItems ?? [],
      };

      state.blockDesign = {
        view: blockDesign.view ?? 'card',
      };

      state.fullBlockDesign = blockDesign;
    },
    formClear: () => initialState,

    formUpdateIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    formUpdateIsActive: (state, action) => {
      state.isActive = action.payload;
    },
    formUpdateBlockContent: (state, action) => {
      state.blockContent = action.payload;
    },
    formUpdateBlockDesign: (state, action) => {
      state.blockDesign = action.payload;
    },
    formUpdatePreviewActive: (state, action) => {
      state.blockPreview.isActive = action.payload;
    },
    formUpdateBlockPreviewView: (state, action) => {
      state.blockPreview.view = action.payload;
    },
    formUpdateBlockPreviewContent: (state, action) => {
      const { field, value } = action.payload;
      state.blockPreview[field] = value;
    },
    formUpdateBlockPreviewDesign: (state, action) => {
      const { field, value } = action.payload;
      state.previewDesign[field] = value;
    },

    // ── Order Form ───────────────────────────────────────────────────────────
    formSetOrderField: (state, action) => {
      const { field, value } = action.payload;
      state.blockContent[field] = value;
    },
    formAddLineItem: (state, action) => {
      const item = action.payload;
      state.blockContent.lineItems.push(item);
    },
    formUpdateLineItem: (state, action) => {
      const { id, patch } = action.payload;
      const idx = state.blockContent.lineItems.findIndex((x) => x.id === id);
      if (idx !== -1) state.blockContent.lineItems[idx] = { ...state.blockContent.lineItems[idx], ...patch };
    },
    formRemoveLineItem: (state, action) => {
      const id = action.payload;
      state.blockContent.lineItems = state.blockContent.lineItems.filter((x) => x.id !== id);
    },
    formReorderLineItems: (state, action) => {
      state.blockContent.lineItems = action.payload;
    },
  },
});

export const {
  formStore,
  formClear,
  formUpdateIsLoading,
  formUpdateIsActive,
  formUpdateBlockContent,
  formUpdateBlockDesign,
  formUpdatePreviewActive,
  formUpdateBlockPreviewView,
  formUpdateBlockPreviewContent,
  formUpdateBlockPreviewDesign,
  formSetOrderField,
  formAddLineItem,
  formUpdateLineItem,
  formRemoveLineItem,
  formReorderLineItems,
} = formSlice.actions;

export const selectFormIsLoading = (state) => state.form.isLoading;

export const selectFormId = (state) => state.form.id;
export const selectFormUID = (state) => state.form.uid;
export const selectFormSID = (state) => state.form.sid;

export const selectFormBlockContent = (state) => state.form.blockContent;

export const selectFormSortItems = createSelector([selectFormBlockContent], ({ items }) => {
  return sortItems(items);
});

export const selectFormData = (state) => state.form.blockContent.data;

export const selectFormFullBlockDesign = (state) => state.form.fullBlockDesign;
export const selectFormBlockDesign = (state) => state.form.blockDesign;
export const selectFormItemDesign = (state) => state.form.itemDesign;
export const selectFormButtonDesign = (state) => state.form.buttonDesign;
export const selectFormContainerDesign = (state) => state.form.containerDesign;
export const selectFormPreviewActive = (state) => state.form.blockPreview.isActive;
export const selectFormPreviewView = (state) => state.form.blockPreview.view;
export const selectFormPreviewContent = (state) => state.form.blockPreview;
export const selectFormPreviewDesign = (state) => state.form.previewDesign;

// Order Form selectors
export const selectFormIsOrderType = (state) => state.form.blockContent.formType === 'order';
export const selectFormLineItems = (state) => state.form.blockContent.lineItems;
export const selectFormOrderSettings = createSelector([selectFormBlockContent], (bc) => ({
  formType: bc.formType,
  paymentMode: bc.paymentMode,
  currency: bc.currency,
  taxRate: bc.taxRate,
  shippingFee: bc.shippingFee,
  depositPercent: bc.depositPercent,
}));

export default formSlice.reducer;
